/**
 * `@shn/substrate-types/fhir-composition` — extracted from monolithic `index.ts` per
 * CTO Platform Hardening Cycle Plan §4 directive 2026-05-24 (substrate-types
 * per-section split). Public API preservation forward: re-exported via barrel
 * `./index.ts`; consumers continue to import from `@shn/substrate-types`.
 */
/**
 * Construct a `FhirProfileReference` from a string. Permissive non-empty
 * check per `reference_substrate_types_existing_primitives.md` brand pattern
 * canonical (FHIR/HL7 reference strings are structured-but-loose external
 * standard references, not cryptographic hashes).
 */
export function asFhirProfileReference(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError('FhirProfileReference must be a non-empty string');
    }
    return value;
}
/**
 * Type guard for `FhirProfileReference`. Permissive non-empty check.
 */
export function isFhirProfileReference(value) {
    return typeof value === 'string' && value.length > 0;
}
/**
 * Construct a `FhirResourceType` from a string. Permissive non-empty check.
 */
export function asFhirResourceType(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError('FhirResourceType must be a non-empty string');
    }
    return value;
}
/**
 * Type guard for `FhirResourceType`. Permissive non-empty check.
 */
export function isFhirResourceType(value) {
    return typeof value === 'string' && value.length > 0;
}
/**
 * Construct a `Hl7ProfileFamilyId` from a string. Permissive non-empty check.
 */
export function asHl7ProfileFamilyId(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError('Hl7ProfileFamilyId must be a non-empty string');
    }
    return value;
}
/**
 * Type guard for `Hl7ProfileFamilyId`. Permissive non-empty check.
 */
export function isHl7ProfileFamilyId(value) {
    return typeof value === 'string' && value.length > 0;
}
/**
 * Construct a `ClinicalAttachmentRef` from a string. Permissive non-empty check.
 */
export function asClinicalAttachmentRef(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError('ClinicalAttachmentRef must be a non-empty string');
    }
    return value;
}
/**
 * Type guard for `ClinicalAttachmentRef`. Permissive non-empty check.
 */
export function isClinicalAttachmentRef(value) {
    return typeof value === 'string' && value.length > 0;
}
/**
 * Construct a `PaAuthorizationRef` from a string. Permissive non-empty check.
 */
export function asPaAuthorizationRef(value) {
    if (typeof value !== 'string' || value.length === 0) {
        throw new TypeError('PaAuthorizationRef must be a non-empty string');
    }
    return value;
}
/**
 * Type guard for `PaAuthorizationRef`. Permissive non-empty check.
 */
export function isPaAuthorizationRef(value) {
    return typeof value === 'string' && value.length > 0;
}
/**
 * `HL7_PROFILE_FAMILIES` — 17 canonical HL7 Profile family identifiers
 * (typed via `asHl7ProfileFamilyId`) per IE FHIR Accelerator IG Composition
 * Reference v0.2 §K.2 (13 canonical) + IE v0.2 supersession cohort closure
 * (4 additional family identifiers — DaVinci-Member-Attribution +
 * DaVinci-Risk-Adjustment + HL7 Helios + HL7 Vulcan).
 *
 * Canonical preservation forward at substrate-types canonical wire-contract
 * types layer; fixture module + tests reference these constants directly to
 * preserve canonical preservation forward (single source of truth).
 */
export const HL7_PROFILE_FAMILIES = {
    US_CORE: asHl7ProfileFamilyId('us-core'),
    DAVINCI_PAS: asHl7ProfileFamilyId('davinci-pas'),
    DAVINCI_CRD: asHl7ProfileFamilyId('davinci-crd'),
    DAVINCI_DTR: asHl7ProfileFamilyId('davinci-dtr'),
    DAVINCI_CDEX: asHl7ProfileFamilyId('davinci-cdex'),
    DAVINCI_HREX: asHl7ProfileFamilyId('davinci-hrex'),
    DAVINCI_PCDE: asHl7ProfileFamilyId('davinci-pcde'),
    DAVINCI_PDEX: asHl7ProfileFamilyId('davinci-pdex'),
    DAVINCI_DEQM: asHl7ProfileFamilyId('davinci-deqm'),
    DAVINCI_MEMBER_ATTRIBUTION: asHl7ProfileFamilyId('davinci-member-attribution'),
    DAVINCI_RISK_ADJUSTMENT: asHl7ProfileFamilyId('davinci-risk-adjustment'),
    CARIN_BB: asHl7ProfileFamilyId('carin-bb'),
    HELIOS: asHl7ProfileFamilyId('helios'),
    FAST: asHl7ProfileFamilyId('fast'),
    CODEX: asHl7ProfileFamilyId('codex'),
    GRAVITY: asHl7ProfileFamilyId('gravity'),
    VULCAN: asHl7ProfileFamilyId('vulcan'),
};
/**
 * `DAVINCI_PAS_PROFILES` — canonical Da Vinci PAS FHIR Profile URL constants
 * (typed via `asFhirProfileReference`) per IE Da Vinci PAS FHIR resource
 * composition reference v0.2 §1.2/§1.3/§1.5.
 *
 * Composes with uc-22-imaging-encounter.test.ts (Steps 3-5) + uc-davinci-pa.test.ts
 * (PAS submission + PAS decision) substrate-side test IMPL canonical preservation
 * forward.
 */
export const DAVINCI_PAS_PROFILES = {
    REQUEST_BUNDLE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-pas/StructureDefinition/profile-pas-request-bundle'),
    RESPONSE_BUNDLE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-pas/StructureDefinition/profile-pas-response-bundle'),
    CLAIM: asFhirProfileReference('http://hl7.org/fhir/us/davinci-pas/StructureDefinition/profile-claim'),
    CLAIM_RESPONSE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-pas/StructureDefinition/profile-claimresponse'),
};
/**
 * `DAVINCI_CRD_PROFILES` — canonical Da Vinci CRD FHIR Profile URL constants
 * (typed via `asFhirProfileReference`) per IE Da Vinci PAS FHIR resource
 * composition reference v0.2 §1.1 (Coverage discovery + eligibility-and-
 * coverage-policy query).
 */
export const DAVINCI_CRD_PROFILES = {
    COVERAGE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-crd/StructureDefinition/profile-coverage'),
};
/**
 * `DAVINCI_DTR_PROFILES` — canonical Da Vinci DTR FHIR Profile URL constants
 * (typed via `asFhirProfileReference`) per IE Da Vinci PAS FHIR resource
 * composition reference v0.2 §1.4 (Documentation Templates and Rules
 * Questionnaire + QuestionnaireResponse).
 */
export const DAVINCI_DTR_PROFILES = {
    QUESTIONNAIRE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/dtr-questionnaire'),
    QUESTIONNAIRE_RESPONSE: asFhirProfileReference('http://hl7.org/fhir/us/davinci-dtr/StructureDefinition/dtr-questionnaireresponse'),
};
/**
 * `US_CORE_PROFILES` — canonical US Core FHIR Profile URL constants
 * (typed via `asFhirProfileReference`) per IE Da Vinci PAS FHIR resource
 * composition reference v0.2 §2 composition matrix (substantively-load-
 * bearing at Patient + Coverage Bundle.entry canonical preservation forward).
 */
export const US_CORE_PROFILES = {
    PATIENT: asFhirProfileReference('http://hl7.org/fhir/us/core/StructureDefinition/us-core-patient'),
    COVERAGE: asFhirProfileReference('http://hl7.org/fhir/us/core/StructureDefinition/us-core-coverage'),
};
//# sourceMappingURL=fhir-composition.js.map